import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Toast system - top-right stack, auto-dismiss after 4s (spec §12).
 *
 * Usage:
 *   1. Wrap the app in <ToastProvider /> (already mounted in main.jsx).
 *   2. Anywhere below: const toast = useToast(); toast.success('Saved');
 *
 * Variants: success (emerald), error (red), info (slate), warning (amber).
 */

const ToastCtx = createContext(null);

let nextId = 1;

const VARIANT_STYLES = {
  success: 'border-emerald-500 bg-white text-slate-900',
  error:   'border-red-600     bg-white text-slate-900',
  warning: 'border-amber-500   bg-white text-slate-900',
  info:    'border-slate-400   bg-white text-slate-900',
};

const VARIANT_ICON = {
  success: '✓',
  error:   '✕',
  warning: '!',
  info:    'i',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback((variant, message, opts = {}) => {
    const id = nextId++;
    const duration = opts.duration ?? 4000;
    setToasts((prev) => [...prev, { id, variant, message }]);
    const handle = setTimeout(() => dismiss(id), duration);
    timers.current.set(id, handle);
    return id;
  }, [dismiss]);

  // Cleanup on unmount.
  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    timers.current.clear();
  }, []);

  const api = {
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error',   msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    info:    (msg, opts) => push('info',    msg, opts),
    dismiss,
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-[1000] flex flex-col gap-2 sm:w-80 pointer-events-none">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-2xl shadow-strong border-l-4 animate-slide-up ${VARIANT_STYLES[t.variant]}`}
            >
              <span
                aria-hidden="true"
                className={`flex-none w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                  t.variant === 'success' ? 'bg-emerald-100 text-emerald-700' :
                  t.variant === 'error'   ? 'bg-red-100 text-red-700' :
                  t.variant === 'warning' ? 'bg-amber-100 text-amber-700' :
                                            'bg-slate-100 text-slate-700'
                }`}
              >{VARIANT_ICON[t.variant]}</span>
              <p className="flex-1 text-sm">{t.message}</p>
              <button
                onClick={() => dismiss(t.id)}
                className="text-slate-400 hover:text-slate-600 text-sm leading-none"
                aria-label="Dismiss"
              >×</button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
